/**
 * NMU-574: a menu item's destination follows its type, in the record and not
 * only on screen.
 *
 * The Menu form shows one target field per ประเภท — URL for ลิงค์(URL),
 * ประเภทข่าว for ข่าว, เนื้อหา for เนื้อหา — and switching the type only ever
 * swapped which field was *visible*. Whatever the previous type had pointed at
 * stayed on the node and was saved with it, so a record could say ประเภท = ข่าว
 * and still carry the URL of a single article.
 *
 * That is what uat's data looks like today: every one of the five ข่าว items
 * carries `url: https://www.nmu.ac.th/news?newsType=…` left over from before it
 * was a NEWS item, and one item carries both a `url` and a `templateId`. The
 * site is not misled by it — nmu-web resolves a NEWS item through its category
 * (NMU-559) and never reads that url — but the record still says two
 * contradictory things about where the item goes, and anything that reads it
 * without knowing the precedence rule follows the article link.
 *
 * So: picking a type clears the targets that type does not use.
 */

/** The one field each ประเภท points with. A type with no entry points nowhere. */
const TARGET_FIELD = {
  URL: 'url',
  NEWS: 'newsType',
  CONTENT: 'templateId',
};

/** Every field a menu node can hold a destination in. */
export const MENU_TARGET_FIELDS = ['url', 'newsType', 'templateId'];

/**
 * NMU-609: fields that belong to one ประเภท and to no other.
 *
 * เปิดในแท็บใหม่ is offered only for ลิงค์(URL) — a เนื้อหา page and a ข่าว
 * listing are both routes inside this site, and opening one in a new tab would
 * leave the visitor with two copies of NMU. So it follows the same rule the
 * targets do: switching away from URL clears it, rather than leaving a flag on
 * the record that the form no longer shows and the site would still read.
 */
const TYPE_OWNED_FLAGS = {
  URL: { enableNewTab: false },
};

/**
 * The field the given type keeps, or null when the type points nowhere.
 *
 * An unset type is a heading (NMU-560), which renders as plain text and has no
 * target at all — so it keeps none of them.
 */
export const targetFieldFor = (menuType) =>
  TARGET_FIELD[String(menuType ?? '').trim().toUpperCase()] ?? null;

/**
 * The targets to clear when an item becomes `menuType`.
 *
 * Returned rather than applied so the caller can put them on the node and in
 * the form in one pass, and so the rule can be read on its own.
 */
export const clearedTargetsFor = (menuType) => {
  const keep = targetFieldFor(menuType);
  const targets = MENU_TARGET_FIELDS.filter((field) => field !== keep).reduce(
    (cleared, field) => ({ ...cleared, [field]: null }),
    {},
  );

  // NMU-609: and the flags this type does not own, reset to their default.
  const type = String(menuType ?? '').trim().toUpperCase();
  const flags = Object.entries(TYPE_OWNED_FLAGS)
    .filter(([owner]) => owner !== type)
    .reduce((cleared, [, defaults]) => ({ ...cleared, ...defaults }), {});

  return { ...targets, ...flags };
};
